import React, { useState, useEffect, useMemo } from 'react';
import BrandController from './controllers/brand-controller';
import Modal from '../../components/Modal';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

// Reusable image picker for a brand (logo / visuel). Defined at module level so it
// keeps its identity across parent re-renders (avoids input remount / focus loss).
const BrandImageField = ({ preview, onSelect, onClear, isSubmitting }) => {
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => onSelect(file, reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        Image de la marque
      </label>
      <div className="mt-1 flex items-center gap-4">
        <div className="h-20 w-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="Aperçu" className="h-full w-full object-cover" />
          ) : (
            <svg className="h-8 w-8 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}>
            <svg className="-ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {preview ? "Changer l'image" : "Choisir une image"}
            <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={isSubmitting} />
          </label>
          {preview && (
            <button
              type="button"
              onClick={onClear}
              disabled={isSubmitting}
              className="text-sm text-red-600 hover:text-red-800 text-left"
            >
              Retirer l'image
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const BrandsPage = () => {
  // State for the brands page
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newBrandImage, setNewBrandImage] = useState(null);
  const [newBrandImagePreview, setNewBrandImagePreview] = useState(null);
  const [newBrandPixelId, setNewBrandPixelId] = useState('');
  const [newBrandToken, setNewBrandToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editedBrandName, setEditedBrandName] = useState('');
  const [editedBrandImage, setEditedBrandImage] = useState(null);
  const [editedBrandImagePreview, setEditedBrandImagePreview] = useState(null);
  const [editedBrandPixelId, setEditedBrandPixelId] = useState('');
  const [editedBrandToken, setEditedBrandToken] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isViewBrandModalOpen, setIsViewBrandModalOpen] = useState(false);
  const [viewedBrand, setViewedBrand] = useState(null);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const {hasPermission} = usePermissionViewModel('marque')


  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const response = await BrandController.fetchBrands(currentPage, pageSize, searchTerm);
        console.log('API response:', response);

        // Extract data from response
        setBrands(response.list || []);
        setTotalItems(response.total || 0);
        setMaxPage(response.maxPage || 1);

        setError(null);
      } catch (err) {
        console.error('Error fetching brands:', err);
        setError('Erreur lors du chargement des marques. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [currentPage, pageSize, searchTerm]);

  // Calculate pagination values
  const totalFilteredItems = useMemo(() => totalItems, [totalItems]);
  const maxFilteredPage = useMemo(() => maxPage, [maxPage]);

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle page size change
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle create brand (placeholder function)
  const handleCreateBrand = () => {
    setNewBrandName('');
    setNewBrandImage(null);
    setNewBrandImagePreview(null);
    setNewBrandPixelId('');
    setNewBrandToken('');
    setIsCreateModalOpen(true);
  };

  // List of available page sizes
  const pageSizeOptions = [5, 10, 20, 50];

  // Helper function to get initials from brand name
  const getInitials = (name) => {
    if (!name) return 'NA'; // Fallback if name is undefined

    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to generate a consistent background color based on brand name
  const getBrandColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800',
      'bg-red-100 text-red-800',
      'bg-indigo-100 text-indigo-800',
      'bg-purple-100 text-purple-800',
      'bg-pink-100 text-pink-800',
      'bg-gray-100 text-gray-800',
    ];

    if (!name) return colors[0]; // Fallback if name is undefined

    // Simple hash function to get a consistent color for the same brand name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // Helper function for rendering page numbers
  const renderPageNumbers = () => {
    if (maxFilteredPage <= 1) {
      return null;
    }

    // Function to render a single page button
    const renderPageButton = (pageNum) => (
      <button
        key={pageNum}
        onClick={() => setCurrentPage(pageNum)}
        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === pageNum
            ? 'z-10 bg-[#174540]/30 border-[#174540] text-[#174540]'
            : 'text-gray-700 hover:bg-gray-50'
          }`}
        aria-current={currentPage === pageNum ? 'page' : undefined}
      >
        {pageNum}
      </button>
    );

    // For small number of pages, show all page numbers
    if (maxFilteredPage <= 7) {
      return [...Array(maxFilteredPage)].map((_, i) => renderPageButton(i + 1));
    }

    // For many pages, show with ellipsis
    const pages = [];

    // Always show first page
    pages.push(renderPageButton(1));

    // Show ellipsis if needed
    if (currentPage > 3) {
      pages.push(
        <span key="ellipsis-1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }

    // Show current page and neighbors
    const startNeighbor = Math.max(2, currentPage - 1);
    const endNeighbor = Math.min(maxFilteredPage - 1, currentPage + 1);

    for (let i = startNeighbor; i <= endNeighbor; i++) {
      pages.push(renderPageButton(i));
    }

    // Show ellipsis if needed
    if (currentPage < maxFilteredPage - 2) {
      pages.push(
        <span key="ellipsis-2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
          ...
        </span>
      );
    }

    // Always show last page
    pages.push(renderPageButton(maxFilteredPage));

    return pages;
  };

  const handleSubmitNewBrand = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setErrors({});
    setSuccessMessage('');

    if (!newBrandName.trim()) {
      setErrors({ name: 'Le nom de la marque est requis' });
      return;
    }

    setIsSubmitting(true);

    try {
      await BrandController.createBrand({
        name: newBrandName.trim(),
        image: newBrandImage,
        pixelId: newBrandPixelId.trim() || null,
        token: newBrandToken.trim() || null,
      });

      // Close modal and refresh data
      setIsCreateModalOpen(false);

      // Refresh the brand list
      const response = await BrandController.fetchBrands(1, pageSize, searchTerm);
      setBrands(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
      setCurrentPage(1);

      // Show success message
      setSuccessMessage(`La marque "${newBrandName.trim()}" a été créée avec succès.`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);

    } catch (error) {
      console.error('Error creating brand:', error);

      // Set appropriate error based on response
      if (error.response?.data?.message) {
        setErrors({ api: error.response.data.message });
      } else {
        setErrors({ api: 'Erreur lors de la création de la marque' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const CreateBrandForm = ({ brandName, setBrandName, isSubmitting }) => {
    // Handle input change locally first to prevent focus loss
    const handleInputChange = (e) => {
      const value = e.target.value;
      setBrandName(value);
    };

    // Prevent form submission on Enter key to avoid premature form submission
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    return (
      <form id="createBrandForm" onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-4">
          <div>
            <label htmlFor="brandName" className="block text-sm font-medium text-gray-700">
              Nom de la marque
            </label>
            <input
              type="text"
              name="brandName"
              id="brandName"
              value={brandName}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className={`mt-1 block w-full border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm`}
              placeholder="Entrez le nom de la marque"
              autoFocus
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <BrandImageField
            preview={newBrandImagePreview}
            onSelect={(file, preview) => {
              setNewBrandImage(file);
              setNewBrandImagePreview(preview);
            }}
            onClear={() => {
              setNewBrandImage(null);
              setNewBrandImagePreview(null);
            }}
            isSubmitting={isSubmitting}
          />

          <div>
            <label htmlFor="brandPixelId" className="block text-sm font-medium text-gray-700">
              Facebook Pixel ID
            </label>
            <input
              type="text"
              name="brandPixelId"
              id="brandPixelId"
              value={newBrandPixelId}
              onChange={(e) => setNewBrandPixelId(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              placeholder="Ex: 627775570354762"
            />
          </div>

          <div>
            <label htmlFor="brandToken" className="block text-sm font-medium text-gray-700">
              Facebook Token (Conversion API)
            </label>
            <input
              type="text"
              name="brandToken"
              id="brandToken"
              value={newBrandToken}
              onChange={(e) => setNewBrandToken(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              placeholder="Jeton d'accès de l'API Conversions"
            />
          </div>
        </div>

        {/* Debug info */}
        {Object.keys(errors).length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
            <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
            <ul className="mt-2 text-sm text-red-700">
              {Object.entries(errors).map(([key, value]) => (
                <li key={key}>{key}: {value}</li>
              ))}
            </ul>
          </div>
        )}
      </form>
    );
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setEditedBrandName(brand.name);
    setEditedBrandImage(null);
    setEditedBrandImagePreview(brand.imagePath || null);
    setEditedBrandPixelId(brand.pixelId || '');
    setEditedBrandToken(''); // token is never returned by the API; empty = keep current
    setIsEditModalOpen(true);
  };

  const handleSubmitEditBrand = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setErrors({});
    setSuccessMessage('');

    if (!editedBrandName.trim()) {
      setErrors({ name: 'Le nom de la marque est requis' });
      return;
    }

    setIsSubmitting(true);

    try {
      await BrandController.updateBrand({
        id: editingBrand.id,
        name: editedBrandName.trim(),
        image: editedBrandImage,
        imagePath: editingBrand.imagePath || null,
        pixelId: editedBrandPixelId.trim() || null,
        token: editedBrandToken.trim() || null,
      });

      // Close modal and refresh data
      setIsEditModalOpen(false);

      // Refresh the brand list
      const response = await BrandController.fetchBrands(currentPage, pageSize, searchTerm);
      setBrands(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);

      // Show success message
      setSuccessMessage(`La marque a été renommée en "${editedBrandName.trim()}" avec succès.`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);

    } catch (error) {
      console.error('Error updating brand:', error);

      if (error.response?.data?.message) {
        setErrors({ api: error.response.data.message });
      } else {
        setErrors({ api: 'Erreur lors de la modification de la marque' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const EditBrandForm = ({ brandName, setBrandName, isSubmitting }) => {
    // Handle input change locally first to prevent focus loss
    const handleInputChange = (e) => {
      const value = e.target.value;
      setBrandName(value);
    };

    // Prevent form submission on Enter key
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    return (
      <form id="editBrandForm" onSubmit={(e) => e.preventDefault()}>
        <div className="space-y-4">
          <div>
            <label htmlFor="editBrandName" className="block text-sm font-medium text-gray-700">
              Nom de la marque
            </label>
            <input
              type="text"
              name="editBrandName"
              id="editBrandName"
              value={brandName}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className={`mt-1 block w-full border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm`}
              placeholder="Entrez le nom de la marque"
              autoFocus
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <BrandImageField
            preview={editedBrandImagePreview}
            onSelect={(file, preview) => {
              setEditedBrandImage(file);
              setEditedBrandImagePreview(preview);
            }}
            onClear={() => {
              setEditedBrandImage(null);
              setEditedBrandImagePreview(null);
              if (editingBrand) editingBrand.imagePath = null;
            }}
            isSubmitting={isSubmitting}
          />

          <div>
            <label htmlFor="editBrandPixelId" className="block text-sm font-medium text-gray-700">
              Facebook Pixel ID
            </label>
            <input
              type="text"
              name="editBrandPixelId"
              id="editBrandPixelId"
              value={editedBrandPixelId}
              onChange={(e) => setEditedBrandPixelId(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              placeholder="Ex: 627775570354762"
            />
          </div>

          <div>
            <label htmlFor="editBrandToken" className="block text-sm font-medium text-gray-700">
              Facebook Token (Conversion API)
            </label>
            <input
              type="text"
              name="editBrandToken"
              id="editBrandToken"
              value={editedBrandToken}
              onChange={(e) => setEditedBrandToken(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
              placeholder={editingBrand?.hasToken ? 'Laisser vide pour conserver le jeton actuel' : "Jeton d'accès de l'API Conversions"}
            />
          </div>
        </div>

        {/* Debug info */}
        {Object.keys(errors).length > 0 && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
            <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
            <ul className="mt-2 text-sm text-red-700">
              {Object.entries(errors).map(([key, value]) => (
                <li key={key}>{key}: {value}</li>
              ))}
            </ul>
          </div>
        )}
      </form>
    );
  };

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!brandToDelete) return;

    // Clear previous messages
    setErrors({});
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      await BrandController.deleteBrand(brandToDelete.id);

      // Close modal
      setIsDeleteModalOpen(false);

      // Save the name for the success message
      const deletedBrandName = brandToDelete.name;

      // Refresh the brand list - check if we need to go to a previous page
      const isLastItemOnPage = brands.length === 1 && currentPage > 1;
      const newPage = isLastItemOnPage ? currentPage - 1 : currentPage;

      // Refresh data
      const response = await BrandController.fetchBrands(newPage, pageSize, searchTerm);
      setBrands(response.list || []);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);

      // If it was the last item on the page, go back one page
      if (isLastItemOnPage) {
        setCurrentPage(newPage);
      }

      // Show success message
      setSuccessMessage(`La marque "${deletedBrandName}" a été supprimée avec succès.`);

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);

    } catch (error) {
      console.error('Error deleting brand:', error);

      if (error.response?.data?.message) {
        setErrors({ api: error.response.data.message });
      } else {
        setErrors({ api: 'Erreur lors de la suppression de la marque' });
      }
    } finally {
      setIsSubmitting(false);
      setBrandToDelete(null);
    }
  };

  const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, brand, isSubmitting }) => {
    if (!brand) return null;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Confirmer la suppression"
        onSubmit={onConfirm}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Supprimer"
        submitColor="red"
      >
        <div className="py-4">
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer la marque <span className="font-medium">{brand.name}</span> ?
          </p>
          <p className="mt-2 text-sm text-gray-500">
            Cette action est irréversible et supprimera définitivement la marque de la base de données.
          </p>

          {/* Debug info */}
          {Object.keys(errors).length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
              <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
              <ul className="mt-2 text-sm text-red-700">
                {Object.entries(errors).map(([key, value]) => (
                  <li key={key}>{key}: {value}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  const handleViewBrand = async (brand) => {
    try {
      setLoading(true);
      setErrors({});

      // Fetch the complete brand details by ID
      const detailedBrand = await BrandController.getBrandById(brand.id);
      setViewedBrand(detailedBrand);
      setIsViewBrandModalOpen(true);
    } catch (error) {
      console.error('Error fetching brand details:', error);

      if (error.response?.data?.message) {
        setErrors({ api: error.response.data.message });
      } else {
        setErrors({ api: 'Erreur lors du chargement des détails de la marque' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marques</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérer les marques disponibles dans votre boutique.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={handleCreateBrand}
            disabled={!hasPermission('write')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md 
            shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none 
            focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] 
            disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed" 
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouvelle Marque
          </button>
        </div>
      </div>

      {/* Add error display at the top level if needed */}
      {Object.keys(errors).length > 0 && errors.api && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <h4 className="text-red-800 font-medium">Erreur:</h4>
          <p className="mt-1 text-sm text-red-700">{errors.api}</p>
        </div>
      )}

      {/* Add success message display */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
          <h4 className="text-green-800 font-medium">Succès:</h4>
          <p className="mt-1 text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
            placeholder="Rechercher une marque par nom..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-2">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-[#174540] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : brands.length === 0 ? (
          <div className="text-center py-12 bg-white shadow overflow-hidden rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune marque trouvée</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? "Essayez d'autres termes de recherche." : "Commencez par créer une nouvelle marque."}
            </p>
            <div className="mt-6">
              <button
                type="button"
                disabled={!hasPermission('write')}
                onClick={handleCreateBrand}
                className="inline-flex items-center px-4 py-2 border border-transparent
                 shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] 
                 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
                 
              >
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Créer une marque
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {brands.map((brand) => (
                <li key={brand.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors duration-150 ease-in-out">
                    {/* Make this flex column on mobile, row on larger screens */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <div className="flex items-center">
                        {/* Brand avatar: image if available, otherwise initials */}
                        {brand.imagePath ? (
                          <img
                            src={brand.imagePath}
                            alt={brand.name}
                            className="flex-shrink-0 h-12 w-12 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${getBrandColor(brand.name)}`}>
                            <span className="text-lg font-medium">{getInitials(brand.name)}</span>
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-lg font-medium text-gray-900">{brand.name}</div>
                        </div>
                      </div>

                      {/* Make buttons responsive - full width on mobile */}
                      <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:space-x-3 w-full sm:w-auto justify-end">
                        {/* View button */}
                        <button 
                          onClick={() => handleViewBrand(brand)}
                          className="flex-1 sm:flex-none inline-flex justify-center sm:justify-start items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                          title="Voir les détails"
                        >
                          <svg className="h-4 w-4 text-gray-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="sm:inline">Voir</span>
                        </button>

                        {/* Edit button */}
                        <button
                          onClick={() => handleEditBrand(brand)}
                          disabled={!hasPermission("edit")}
                          className="flex-1 sm:flex-none inline-flex justify-center sm:justify-start items-center 
                          px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md 
                          text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
                          focus:ring-[#174540] disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
                          title="Éditer la marque"
                        >
                          <svg className="h-4 w-4 text-gray-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          <span className="sm:inline">Éditer</span>
                        </button>

                        {/* Delete button */}
                        <button
                          onClick={() => handleDeleteClick(brand)}
                          disabled={!hasPermission("delete")}

                          className="flex-1 sm:flex-none inline-flex justify-center sm:justify-start items-center
                           px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700
                            bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
                          title="Supprimer la marque"
                        >
                          <svg className="h-4 w-4 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="sm:inline">Supprimer</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && brands.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          {/* Mobile pagination */}
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
            >
              Précédent
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(maxFilteredPage, currentPage + 1))}
              disabled={currentPage === maxFilteredPage}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === maxFilteredPage
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
            >
              Suivant
            </button>
          </div>

          {/* Desktop pagination */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center">
              <p className="text-sm text-gray-700 mr-4">
                Affichage de{' '}
                <span className="font-medium">
                  {totalFilteredItems > 0 ? (currentPage - 1) * pageSize + 1 : 0}
                </span>
                {' '}à{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, totalFilteredItems)}
                </span>
                {' '}sur{' '}
                <span className="font-medium">{totalFilteredItems}</span> marques
              </p>

              {/* Page size selector */}
              <div className="flex items-center">
                <label htmlFor="pageSize" className="text-sm text-gray-700 mr-2">
                  Afficher :
                </label>
                <select
                  id="pageSize"
                  name="pageSize"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="h-full rounded-md border border-gray-300 bg-white py-0 pl-2 pr-7 text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#174540] sm:text-sm"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Page navigation */}
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                {/* First page button */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  title="Première page"
                >
                  <span className="sr-only">Première page</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Previous page button */}
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  title="Page précédente"
                >
                  <span className="sr-only">Précédent</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Page numbers */}
                {renderPageNumbers()}

                {/* Next page button */}
                <button
                  onClick={() => setCurrentPage(Math.min(maxFilteredPage, currentPage + 1))}
                  disabled={currentPage === maxFilteredPage}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === maxFilteredPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  title="Page suivante"
                >
                  <span className="sr-only">Suivant</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10l-3.293-3.293a1 1 0 011.414-1.414l-4 4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Last page button */}
                <button
                  onClick={() => setCurrentPage(maxFilteredPage)}
                  disabled={currentPage === maxFilteredPage}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === maxFilteredPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  title="Dernière page"
                >
                  <span className="sr-only">Dernière page</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Créer une nouvelle marque"
        onSubmit={handleSubmitNewBrand}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Créer"
      >
        <CreateBrandForm
          brandName={newBrandName}
          setBrandName={setNewBrandName}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Modifier la marque"
        onSubmit={handleSubmitEditBrand}
        size="md"
        isSubmitting={isSubmitting}
        submitLabel="Enregistrer"
      >
        <EditBrandForm
          brandName={editedBrandName}
          setBrandName={setEditedBrandName}
          isSubmitting={isSubmitting}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        brand={brandToDelete}
        isSubmitting={isSubmitting}
      />

      {/* View Brand Modal */}
      <Modal
        isOpen={isViewBrandModalOpen}
        onClose={() => setIsViewBrandModalOpen(false)}
        title="Détails de la marque"
        size="lg"
        hideSubmitButton={true}
      >
        {/* Debug info */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
            <h4 className="text-red-800 font-medium">Erreurs de validation:</h4>
            <ul className="mt-2 text-sm text-red-700">
              {Object.entries(errors).map(([key, value]) => (
                <li key={key}>{key}: {value}</li>
              ))}
            </ul>
          </div>
        )}

        {viewedBrand ? (
          <div className="space-y-8">
            {/* Header with name and actions */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div className="flex items-center">
                <div className={`h-16 w-16 rounded-full flex items-center justify-center ${getBrandColor(viewedBrand.name)}`}>
                  {viewedBrand.imagePath ? (
                    <img src={viewedBrand.imagePath} alt={viewedBrand.name} className="h-16 w-16 rounded-full object-cover" />
                  ) : (
                    <span className="text-2xl font-medium">{getInitials(viewedBrand.name)}</span>
                  )}
                </div>
                <div className="ml-5">
                  <h2 className="text-xl font-bold text-gray-900">{viewedBrand.name}</h2>
                  <p className="text-sm text-gray-500">ID: {viewedBrand.id}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setIsViewBrandModalOpen(false);
                    handleEditBrand(viewedBrand);
                  }}
                  disabled={!hasPermission("edit")}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md 
                  shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none
                   focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Modifier
                </button>
                <button
                   disabled={!hasPermission("delete")}

                  onClick={() => {
                    setIsViewBrandModalOpen(false);
                    handleDeleteClick(viewedBrand);
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 
                  rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-gray-50 focus:outline-none 
                  focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
                >
                  <svg className="-ml-1 mr-2 h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              </div>
            </div>

            {/* Brand Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Informations générales
              </h3>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Nom de la marque</p>
                    <p className="text-sm font-semibold text-gray-900">{viewedBrand.name}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Facebook Pixel ID</p>
                    <p className="text-sm font-semibold text-gray-900">{viewedBrand.pixelId || 'Non configuré'}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#174540]/10 flex items-center justify-center">
                    <svg className="h-5 w-5 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <p className="text-xs font-medium text-gray-500 uppercase">Facebook Token</p>
                    <p className="text-sm font-semibold text-gray-900">{viewedBrand.hasToken ? 'Configuré ••••••••' : 'Non configuré'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Products associated with the brand */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Produits associés
              </h3>

              {viewedBrand.products && viewedBrand.products.length > 0 ? (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Nom</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Catégorie</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Prix</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Stock</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {viewedBrand.products.map((product) => (
                        <tr key={product.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {product.name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {product.category}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {product.price}TND
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {product.stock} unités
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">Aucun produit associé à cette marque</p>
                  <button
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
                    onClick={() => setIsViewBrandModalOpen(false)}
                  >
                    Créer un produit
                  </button>
                </div>
              )}
            </div>

            {/* Statistics Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <svg className="h-5 w-5 mr-2 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Statistiques et performances
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-gray-500 uppercase block">Produits</span>
                  <div className="mt-2 flex items-baseline">
                    <span className="text-2xl font-semibold text-gray-900">
                      {viewedBrand.products?.length || 0}
                    </span>
                    <span className="ml-2 text-sm text-gray-600">produits</span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-gray-500 uppercase block">Ventes</span>
                  <div className="mt-2 flex items-baseline">
                    <span className="text-2xl font-semibold text-gray-900">0</span>
                    <span className="ml-2 text-sm text-gray-600">ce mois</span>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-gray-500 uppercase block">Revenu</span>
                  <div className="mt-2 flex items-baseline">
                    <span className="text-2xl font-semibold text-gray-900">0TND</span>
                    <span className="ml-2 text-sm text-gray-600">généré</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#174540]"></div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BrandsPage;